import { useEffect, useRef, useState } from 'react'
import { captureException, captureMessage } from '@sentry/react'
import { ZoomIn, ZoomOut } from 'lucide-react'
import { toast } from 'sonner'

import type { Product } from '@/lib/api/product-api'
import type { ScannerHandle, ZoomCapability } from '@/lib/barcode-camera'
import { productApi } from '@/lib/api/product-api'
import {
    applyContinuousFocus,
    applyZoom,
    createBarcodeScanner,
    getZoomCapability,
    reportIfScannerVideoCollapsed,
    reportScannerCameraDiagnostics,
    triggerRefocus,
} from '@/lib/barcode-camera'
import { SCAN_DEDUP_COOLDOWN_MS, createScanDedup } from '@/hooks/use-scan-dedup'
import { looksLikeUrl } from '@/lib/scan-code'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { m } from '@/lib/paraglide/messages'

interface Props {
    open: boolean
    onOpenChange: (open: boolean) => void
    onProductFound?: (product: Product) => void
    onBarcodeNotFound?: (barcode: string) => void
    onRawScan?: (text: string) => void
    /**
     * Which symbology to decode. 'barcode' (default) restricts to 1D codes
     * used on product packaging + QR. 'qr' restricts to QR codes only.
     */
    formats?: 'barcode' | 'qr'
    /**
     * Continuous mode: keep the camera open and keep recording after each
     * decode (for high-volume stock-in). Default false preserves the one-shot
     * create-order behavior (close on first decode).
     */
    continuous?: boolean
}

// Ship the lens/focus/zoom report as a searchable info event — a blurry
// fixed-focus lens throws no error, so this is the only prod signal saying
// which camera a failing device got bound to.
const captureScannerDiagnostics = (
    message: string,
    data: Record<string, unknown>,
) =>
    captureMessage(message, {
        level: 'info',
        tags: { feature: 'barcode-scanner' },
        extra: data,
    })

/**
 * Camera-based barcode/QR scanner dialog for mobile create-order flow.
 * Uses @zxing/browser with the rear camera. Falls back to manual barcode
 * text entry if camera permission is denied.
 */
export function MobileBarcodeScannerDialog({
    open,
    onOpenChange,
    onProductFound,
    onBarcodeNotFound,
    onRawScan,
    formats = 'barcode',
    continuous = false,
}: Props) {
    const [scanning, setScanning] = useState(false)
    const [manualBarcode, setManualBarcode] = useState('')
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)
    const handleRef = useRef<ScannerHandle | null>(null)
    const videoRef = useRef<HTMLVideoElement | null>(null)
    const isStartingRef = useRef(false)
    const dedupRef = useRef(createScanDedup(SCAN_DEDUP_COOLDOWN_MS))
    const [zoomCap, setZoomCap] = useState<ZoomCapability | null>(null)
    const [zoomLevel, setZoomLevel] = useState(1)
    const zoomRef = useRef(1)
    const pinchRef = useRef<{ dist: number; zoom: number } | null>(null)
    const [focusPulse, setFocusPulse] = useState<{
        x: number
        y: number
    } | null>(null)
    const focusPulseTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

    const handleTapFocus = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!scanning || !handleRef.current) return
        const track = handleRef.current.getVideoTrack()
        if (!track) return
        const rect = e.currentTarget.getBoundingClientRect()
        if (rect.width === 0 || rect.height === 0) return
        const px = e.clientX - rect.left
        const py = e.clientY - rect.top
        const clamp01 = (v: number) => Math.min(1, Math.max(0, v))
        void triggerRefocus(track, {
            x: clamp01(px / rect.width),
            y: clamp01(py / rect.height),
        })
        setFocusPulse({ x: px, y: py })
        if (focusPulseTimer.current) clearTimeout(focusPulseTimer.current)
        focusPulseTimer.current = setTimeout(() => setFocusPulse(null), 700)
    }

    const changeZoom = (value: number) => {
        if (!zoomCap || !handleRef.current) return
        const track = handleRef.current.getVideoTrack()
        if (!track) return
        const clamped = Math.min(zoomCap.max, Math.max(zoomCap.min, value))
        setZoomLevel(clamped)
        zoomRef.current = clamped
        void applyZoom(track, clamped)
    }

    const pinchDistance = (touches: React.TouchList) =>
        Math.hypot(
            touches[0].clientX - touches[1].clientX,
            touches[0].clientY - touches[1].clientY,
        )

    const stopScanner = async () => {
        isStartingRef.current = false
        if (handleRef.current) {
            try {
                await handleRef.current.stop()
            } catch {
                /* already stopped */
            }
            handleRef.current = null
        }
        setScanning(false)
        setZoomCap(null)
        pinchRef.current = null
    }

    const recordBarcode = async (trimmed: string) => {
        if (onRawScan) {
            onRawScan(trimmed)
            return
        }
        // A URL is never a product barcode. Delivery-note QRs and in-app browser
        // links decode to full URLs; sending one to the barcode lookup only 404s
        // — and in continuous mode a held QR re-fires every cooldown, hammering
        // the API. Short-circuit with a hint instead. Stable toast id so a code
        // held in frame refreshes one message rather than stacking many.
        if (looksLikeUrl(trimmed)) {
            toast.error(m.scan_url_not_product(), {
                id: 'scan-url-not-product',
            })
            return
        }
        setLoading(true)
        setError('')
        try {
            const resp = await productApi.getByBarcode(trimmed)
            if (resp.data) {
                if (!continuous) {
                    toast.success(m.pos_barcode_added({ name: resp.data.name }))
                }
                onProductFound?.(resp.data)
            } else {
                onBarcodeNotFound?.(trimmed)
            }
        } catch {
            onBarcodeNotFound?.(trimmed)
        } finally {
            setLoading(false)
        }
    }

    const handleBarcode = async (barcode: string) => {
        const trimmed = barcode.trim()
        if (!trimmed) return
        await stopScanner()
        await recordBarcode(trimmed)
        onOpenChange(false)
    }

    const handleContinuousDecode = (decodedText: string) => {
        const trimmed = decodedText.trim()
        if (!trimmed) return
        if (!dedupRef.current.shouldAccept(trimmed, Date.now())) return
        void recordBarcode(trimmed)
    }

    const startScanner = async () => {
        if (isStartingRef.current || handleRef.current) return
        isStartingRef.current = true
        setError('')
        setScanning(true)
        const videoEl = videoRef.current
        if (!videoEl) {
            isStartingRef.current = false
            return
        }
        try {
            const handle = await createBarcodeScanner({
                videoEl,
                formats: formats === 'qr' ? 'qr-only' : '1d-and-qr',
                onResult: (text) => {
                    if (continuous) {
                        handleContinuousDecode(text)
                    } else {
                        void handleBarcode(text)
                    }
                },
                onError: (err) => captureException(err),
            })
            if (!isStartingRef.current) {
                await handle.stop()
                return
            }
            handleRef.current = handle
            const track = handleRef.current.getVideoTrack()
            if (track) {
                const focusAccepted = await applyContinuousFocus(track)
                const cap = getZoomCapability(track)
                setZoomCap(cap)
                if (cap) {
                    setZoomLevel(cap.min)
                    zoomRef.current = cap.min
                }
                reportScannerCameraDiagnostics(
                    track,
                    focusAccepted,
                    captureScannerDiagnostics,
                )
            }
            reportIfScannerVideoCollapsed(videoEl, captureException)
        } catch (err) {
            captureException(err)
            setScanning(false)
            setError(m.qr_camera_error())
            handleRef.current = null
        } finally {
            isStartingRef.current = false
        }
    }

    const handleManualSubmit = () => {
        const trimmed = manualBarcode.trim()
        if (!trimmed) return
        if (continuous) {
            void recordBarcode(trimmed)
        } else {
            void handleBarcode(trimmed)
        }
        setManualBarcode('')
    }

    useEffect(() => {
        if (open) {
            dedupRef.current = createScanDedup(SCAN_DEDUP_COOLDOWN_MS)
        } else {
            void stopScanner()
            setManualBarcode('')
            setError('')
            if (focusPulseTimer.current) clearTimeout(focusPulseTimer.current)
            setFocusPulse(null)
        }
    }, [open])

    // Clear the focus-pulse timer on unmount so a late fire cannot dispatch a
    // React state update after the test/jsdom environment is torn down —
    // guards against `ReferenceError: window is not defined` in CI.
    useEffect(() => {
        return () => {
            if (focusPulseTimer.current) {
                clearTimeout(focusPulseTimer.current)
                focusPulseTimer.current = null
            }
        }
    }, [])

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="flex flex-col max-w-none w-screen h-[100svh] rounded-none p-4 sm:h-auto sm:w-full sm:max-w-sm sm:rounded-xl sm:p-6">
                <DialogHeader>
                    <DialogTitle>{m.mobile_scan_barcode_button()}</DialogTitle>
                    <DialogDescription>
                        {m.mobile_scan_barcode_desc()}
                    </DialogDescription>
                </DialogHeader>

                <div className="flex flex-1 flex-col gap-4 overflow-y-auto">
                    <div
                        data-testid="mobile-scanner-stage"
                        className="relative w-full overflow-hidden rounded-md bg-black aspect-[3/4] max-h-[55vh] shrink-0 cursor-pointer"
                        style={{ touchAction: zoomCap ? 'none' : undefined }}
                        onClick={handleTapFocus}
                        onTouchStart={(e) => {
                            if (zoomCap && e.touches.length === 2) {
                                pinchRef.current = {
                                    dist: pinchDistance(e.touches),
                                    zoom: zoomRef.current,
                                }
                            }
                        }}
                        onTouchMove={(e) => {
                            const start = pinchRef.current
                            if (zoomCap && start && e.touches.length === 2) {
                                changeZoom(
                                    start.zoom *
                                        (pinchDistance(e.touches) / start.dist),
                                )
                            }
                        }}
                        onTouchEnd={() => {
                            pinchRef.current = null
                        }}
                    >
                        <video
                            ref={videoRef}
                            data-testid="mobile-scanner-video"
                            className="absolute inset-0 h-full w-full object-cover"
                            autoPlay
                            playsInline
                            muted
                        />

                        {scanning && (
                            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                                <div
                                    className="relative aspect-[5/3] w-[78%] max-w-[20rem] rounded-xl"
                                    style={{
                                        boxShadow:
                                            '0 0 0 9999px rgba(0,0,0,0.55)',
                                    }}
                                >
                                    <div className="absolute inset-0 rounded-xl border-2 border-white/80" />
                                    <span className="absolute -left-px -top-px size-7 rounded-tl-xl border-l-4 border-t-4 border-primary" />
                                    <span className="absolute -right-px -top-px size-7 rounded-tr-xl border-r-4 border-t-4 border-primary" />
                                    <span className="absolute -bottom-px -left-px size-7 rounded-bl-xl border-b-4 border-l-4 border-primary" />
                                    <span className="absolute -bottom-px -right-px size-7 rounded-br-xl border-b-4 border-r-4 border-primary" />
                                </div>
                                <p className="mt-4 rounded bg-black/70 px-3 py-1 text-sm font-medium text-white">
                                    {m.mobile_scan_aim_hint()}
                                </p>
                                <p className="mt-1.5 rounded bg-black/70 px-3 py-1 text-xs text-white/90">
                                    {m.mobile_scan_tap_focus_hint()}
                                </p>
                            </div>
                        )}

                        {focusPulse && (
                            <span
                                className="pointer-events-none absolute size-16 -translate-x-1/2 -translate-y-1/2 animate-ping rounded-full border-2 border-primary"
                                style={{
                                    left: focusPulse.x,
                                    top: focusPulse.y,
                                }}
                            />
                        )}

                        {scanning && zoomCap && (
                            <div
                                className="absolute inset-x-0 bottom-0 flex items-center gap-3 bg-black/55 px-4 py-2"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <ZoomOut className="size-5 shrink-0 text-white" />
                                <input
                                    type="range"
                                    min={zoomCap.min}
                                    max={zoomCap.max}
                                    step={zoomCap.step}
                                    value={zoomLevel}
                                    onChange={(e) =>
                                        changeZoom(Number(e.target.value))
                                    }
                                    aria-label="Zoom camera"
                                    className="h-1 w-full cursor-pointer accent-primary"
                                />
                                <ZoomIn className="size-5 shrink-0 text-white" />
                            </div>
                        )}
                    </div>

                    {!scanning ? (
                        <Button
                            className="w-full"
                            onClick={() => void startScanner()}
                            disabled={loading}
                        >
                            {m.mobile_open_camera()}
                        </Button>
                    ) : (
                        <Button
                            variant="outline"
                            className="w-full"
                            onClick={() => void stopScanner()}
                        >
                            {m.mobile_stop_camera()}
                        </Button>
                    )}

                    {error && (
                        <p className="text-sm text-destructive">{error}</p>
                    )}

                    <div className="relative flex items-center gap-2">
                        <div className="flex-1 border-t" />
                        <span className="text-xs text-muted-foreground">
                            {m.common_or_manual_input()}
                        </span>
                        <div className="flex-1 border-t" />
                    </div>

                    <div className="flex gap-2">
                        <Input
                            placeholder={m.mobile_manual_barcode_placeholder()}
                            value={manualBarcode}
                            onChange={(e) => setManualBarcode(e.target.value)}
                            className="text-base"
                            onKeyDown={(e) =>
                                e.key === 'Enter' && handleManualSubmit()
                            }
                        />
                        <Button
                            disabled={!manualBarcode.trim() || loading}
                            onClick={handleManualSubmit}
                        >
                            {m.common_confirm()}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}
